import { ComponentPropsWithoutRef, ComponentRef, CSSProperties, FC, ReactNode, Ref } from 'react';
import classNames from 'classnames';
import { Drawer } from 'vaul';

import BottomSheetBody from './BottomSheetBody/BottomSheetBody';
import BottomSheetClose from './BottomSheetClose/BottomSheetClose';
import BottomSheetFooter from './BottomSheetFooter/BottomSheetFooter';
import BottomSheetHeader from './BottomSheetHeader/BottomSheetHeader';
import styles from './BottomSheet.module.css';

type BottomSheetContentPassthroughProps = Omit<ComponentPropsWithoutRef<typeof Drawer.Content>, 'className' | 'style'>;

export type BottomSheetProps = {
  /** Открыта ли шторка — управляется снаружи. */
  open: boolean;
  /** Вызывается при любой попытке открыть/закрыть (свайп вниз, клик снаружи, Escape, `BottomSheet.Close`). */
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Высота поверх авто-фита под контент (в пределах max-height). */
  height?: number | string;
  style?: CSSProperties;
  /** Класс для оверлея-подложки. */
  overlayClassName?: string;
  /** Класс для самой шторки (контента). */
  contentClassName?: string;
  ref?: Ref<ComponentRef<typeof Drawer.Content>>;
} & BottomSheetContentPassthroughProps;

/** Выезжающая снизу шторка для мобайла на Vaul (обёртка над Radix Dialog). */
const BottomSheet: FC<BottomSheetProps> = ({
  open,
  onOpenChange,
  children,
  height,
  style,
  overlayClassName,
  contentClassName,
  ref,
  'aria-describedby': ariaDescribedBy,
  ...rest
}) => (
  <Drawer.Root
    open={open}
    onOpenChange={onOpenChange}>
    <Drawer.Portal>
      <Drawer.Overlay className={classNames(styles.overlay, overlayClassName)} />
      <Drawer.Content
        ref={ref}
        aria-describedby={ariaDescribedBy}
        {...rest}
        className={classNames(styles.content, contentClassName)}
        style={{ ...style, height }}>
        <Drawer.Handle className={styles.handle} />
        {children}
      </Drawer.Content>
    </Drawer.Portal>
  </Drawer.Root>
);

type BottomSheetCompound = typeof BottomSheet & {
  Header: typeof BottomSheetHeader;
  Body: typeof BottomSheetBody;
  Footer: typeof BottomSheetFooter;
  Close: typeof BottomSheetClose;
};

const CompoundBottomSheet = BottomSheet as BottomSheetCompound;
CompoundBottomSheet.Header = BottomSheetHeader;
CompoundBottomSheet.Body = BottomSheetBody;
CompoundBottomSheet.Footer = BottomSheetFooter;
CompoundBottomSheet.Close = BottomSheetClose;

export default CompoundBottomSheet;
